/**
 * The dashboard — the empty state **is** onboarding, and there is no wizard shell.
 *
 * `screens.md` §2 and design C. A user who abandons signup and comes back lands here,
 * which is why there is nowhere else to land: a separate wizard would be a second place
 * that knows what they were doing, and the two would disagree.
 *
 * ## What this screen refuses to render
 *
 * The usage meters' scan half, the grace / degraded / over-limit / fair-use states, and
 * the 90-day-silent product list. **None of them has a subject in Phase 2** (F7,
 * measured): no entitlement gate exists anywhere in the phase, rollups are Phase 7, and
 * §4 forbids the dashboard reading `scan_events`. Building any of them means inventing
 * data.
 *
 * **The liveness dot left this list at c39** and is the only thing that has. It has a
 * subject now — a public resolver edge — and R10's named alternative, a browser-side
 * ping of that edge's well-known endpoint, is what gives it one. Everything else above
 * still has none.
 *
 * ## One coloured thing, at most
 *
 * The acceptance criterion is *"on a **healthy** dashboard exactly one thing is
 * coloured"*, and from c39 that one thing is the live dot — which is what the criterion's
 * "exactly one" was always reserved for. Before c39 the budget was unspent: the dot was
 * grey and a healthy screen carried no colour at all, so "exactly one" described
 * something that did not exist.
 *
 * Everything else is unchanged and still refuses colour. Catalogue counts are plain
 * text, the header's third action is permanently neutral, and the only *tone* that may
 * appear is `recall` — which appears on a screen that is by definition not healthy, where
 * §12.1's precedence stack governs and a live dot beside it is a second true statement
 * rather than a second claim on the same budget. A second **tone** on a healthy screen is
 * still a defect, not a refinement.
 */
import { useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { Surface, SurfaceBody, SurfaceFooter, SurfaceHeader } from "@packagepage/ui";

import { problemOf } from "../api/client.js";
import { api } from "../api/procedures.js";
import { attentionStack, catalogueCounts, type AttentionItem } from "./attention.js";
import { applicableChecks, summariseChecks, type Check } from "./checks.js";
import { probeEdge, type EdgeLiveness } from "../api/edge.js";
import { presentEdge } from "./liveness.js";
import { countedFromOf } from "./usage-meter.js";

import "./dashboard.css";

/**
 * The dashboard's one alert surface — **one card containing N rows, in both states.**
 *
 * `components.md` → Attention card, RULED 2026-08-11 (Q1). This screen rendered a section
 * title with N separate cards beneath it, and the title said "Needs you" — **a string
 * that appears nowhere in the handoff.** The populated state had never been designed when
 * it was built, so it was invented; the ruling supplies it, and the invention was wrong in
 * both parts. The rule the ruling gives is the reason it matters: *"Healthy and populated
 * are one component whose contents change; that is what stops them drifting apart again."*
 *
 * The header and the sunken footer persist in every state, and the title is always a
 * sentence that answers the question — never a section label.
 *
 * ## Two checks, not six, and the arithmetic follows the set
 *
 * The ruled check set is six. Four of them — domain, billing, imports, usage — have no
 * subject in Phase 2, and rendering them would put green ticks on the screen on the
 * strength of nothing having been looked at. `checks.ts` carries the reasoning and the
 * ruling; every number and sentence here is derived from the set it is given, so a check
 * joins the card the phase it gains a subject and nothing here changes.
 *
 * "On a healthy dashboard exactly one thing is coloured" still governs: this card is
 * neutral, its rows carry the only colour, and a healthy card has no rows.
 */
function AttentionCardSurface({
  stack,
  checks,
}: {
  stack: readonly AttentionItem[];
  checks: readonly Check[];
}): ReactNode {
  const summary = summariseChecks(
    checks,
    stack.map((item) => item.kind),
    stack.filter((item) => item.tone === "neutral").map((item) => item.kind),
  );

  return (
    <Surface sectioned data-screen-label="Attention">
      <SurfaceHeader title={summary.title} meta={summary.meta} />
      <SurfaceBody bleed={stack.length > 0}>
        {stack.length === 0 ? (
          /*
           * The healthy body. The mock's six-row tick grid needs six checks with
           * subjects; with two, a grid of two is a grid pretending to be one, so the
           * clear checks are named in a line instead. The footer already lists them in
           * the populated state, which is where the phrasing comes from.
           */
          <div className="pp-dash__clear">
            {checks.map((check) => (
              <span key={check.id} className="pp-dash__clear-row">
                <span className="pp-dash__tick" aria-hidden="true">
                  ✓
                </span>
                {check.clear}
              </span>
            ))}
          </div>
        ) : (
          <div className="pp-dash__stack" data-screen-label="Attention stack">
            {stack.map((item, index) => (
              <AttentionCard
                key={item.id}
                item={item}
                index={index}
                total={stack.length}
              />
            ))}
          </div>
        )}
      </SurfaceBody>
      <SurfaceFooter>{summary.footer}</SurfaceFooter>
    </Surface>
  );
}

function AttentionCard({
  item,
  index,
  total,
}: {
  item: AttentionItem;
  index: number;
  total: number;
}): ReactNode {
  // `led` — the 3px rule — is reserved for recall content, and the type refuses it for
  // any other tone. The other two are ordinary filled surfaces.
  const surface =
    item.tone === "recall" ? (
      <Surface tone="recall" emphasis="led">
        <AttentionBody item={item} index={index} total={total} />
      </Surface>
    ) : (
      <Surface tone={item.tone} emphasis={item.tone === "neutral" ? "flat" : "filled"}>
        <AttentionBody item={item} index={index} total={total} />
      </Surface>
    );
  return surface;
}

function AttentionBody({
  item,
  index,
  total,
}: {
  item: AttentionItem;
  index: number;
  total: number;
}): ReactNode {
  return (
    <>
      <div className="pp-dash__item-head">
        {item.href === undefined ? (
          <span className="pp-dash__item-title">{item.title}</span>
        ) : (
          <Link to={item.href} className="pp-dash__item-title">
            {item.title}
          </Link>
        )}
        {/* Numbered "so the user knows the list ends" — screens.md §2. */}
        <span className="pp-dash__ordinal">
          {index + 1} of {total}
        </span>
      </div>
      <p className="pp-dash__item-body">{item.body}</p>
    </>
  );
}

/**
 * The strip, with the liveness dot now reporting for the first time.
 *
 * The headline counts codes rather than claiming they answer, which is the honest form
 * of the mock's *"All 34 codes are answering"* when nothing measures whether they are —
 * and that stays true. **The dot makes a narrower claim than the headline, deliberately:
 * the headline is about this catalogue, the dot is about the edge.** Neither is derivable
 * from the other, and merging them would recreate the mock's overreach with a real
 * measurement behind it, which is worse than the mock.
 *
 * R10's alternative — the SPA pinging the resolver edge's well-known endpoint — was
 * parked at Block D as *"worth doing at c39 when a public edge exists; before that it
 * would report the developer's localhost."* This is c39. `resolverBaseUrl` comes from
 * `orgs.current`, which already carries it for *"View live page"*, so the dot needs no
 * new procedure and the tRPC census is untouched.
 *
 * **Two states of the three are unreachable by this package's suite, by construction** —
 * it is `renderToStaticMarkup`, so the query never resolves and the dot renders `unknown`
 * for ever. The rule that follows from c37b is that they owe an E2E flow which
 * *traverses* the gate; `e2e/test/flow-dashboard.spec.ts` is it, driving all three by
 * routing the ping. The mapping itself is pure and lives in `./liveness.js`.
 */
function LiveStrip({ published }: { published: number }): ReactNode {
  const org = useQuery({
    queryKey: ["orgs", "current"],
    queryFn: () => api.currentOrg(),
  });
  const resolverBaseUrl = org.data?.resolverBaseUrl;

  const edge = useQuery({
    queryKey: ["edge-liveness", resolverBaseUrl],
    queryFn: () => probeEdge(resolverBaseUrl as string),
    enabled: resolverBaseUrl !== undefined,
    // `probeEdge` never rejects, so there is nothing to retry and a retry would only
    // delay the honest grey. A dot that spends twelve seconds in `unknown` because
    // TanStack is backing off is a dot that is lying by omission.
    retry: false,
    // A minute is short enough that a recovered edge turns the dot green while the user
    // is still on the screen, and long enough that returning to this tab does not
    // re-ping on every focus. No interval: this is a dashboard, not a status page.
    staleTime: 60_000,
  });

  // `unknown` while the query has not settled — the initial state, and the only one a
  // static render can produce. Never optimistically green.
  const state: EdgeLiveness = edge.data ?? "unknown";
  const dot = presentEdge(state);

  return (
    <Surface>
      <div className="pp-dash__strip">
        <div>
          <div className="pp-dash__strip-headline">
            {/* Decorative: the dot carries no information a screen reader can use, and
                the sentence below says the same thing in words. `data-edge` is the E2E
                flow's handle — an attribute rather than a class assertion, so restyling
                the dot cannot silently retarget the probe. */}
            <span
              className={`pp-dash__dot${dot.modifier}`}
              data-edge={state}
              aria-hidden="true"
            />
            <span className="pp-dash__headline">
              {published === 0
                ? "No codes are published yet"
                : `${published} ${published === 1 ? "code is" : "codes are"} published`}
            </span>
          </div>
          <p className="pp-dash__strip-note">
            {dot.label}{" "}
            {published === 0
              ? "Publishing a product claims its GTIN and starts answering scans within seconds."
              : "This account has no scan history to show yet — resolution figures arrive with analytics."}
          </p>
        </div>
      </div>
    </Surface>
  );
}

/**
 * The empty dashboard, which is the whole of onboarding.
 *
 * The primary action is `Add a product` in the header, which lands on the same honest
 * stub the other two do — c34 builds the real form (ruled 2026-08-06), with
 * `screens.md` §1's check-digit teaching arriving with Phase 3's onboarding work. What
 * this card must not do is describe a screen as though it exists.
 */
function Onboarding(): ReactNode {
  return (
    <Surface>
      <div className="pp-dash__onboard">
        <div className="pp-dash__onboard-title">Let&rsquo;s get one code answering</div>
        <p className="pp-dash__onboard-body">
          Add a product and build its page now, with no card; its code starts answering
          scans once you publish it under a verified company prefix. Everything else can
          wait until you have seen it work.
        </p>
        <div className="pp-dash__placeholder-row">
          <div className="pp-dash__placeholder" aria-hidden="true" />
          <p className="pp-dash__placeholder-note">
            This is where your first code will be.
          </p>
        </div>
      </div>
    </Surface>
  );
}

export function Dashboard(): ReactNode {
  const recalls = useQuery({
    queryKey: ["recalls"],
    queryFn: () => api.recalls(),
  });
  const prefixes = useQuery({
    queryKey: ["prefixes"],
    queryFn: () => api.companyPrefixes(),
  });
  /**
   * The catalogue's totals, from the server's own `GROUP BY`.
   *
   * **Not the product list.** This screen used to read `products.list` and count statuses
   * in the rows it returned, which was right while that endpoint returned everything and
   * became wrong the moment c34 paginated it — a brand with 900 products would have seen
   * its drafts counted out of the first hundred rows. The list answers "a page"; this
   * answers "the catalogue", and neither is derivable from the other.
   */
  const counts = useQuery({
    queryKey: ["products", "counts"],
    queryFn: () => api.productCounts(),
  });
  // U16: the fourth query, and the only one the card reads for a state it cannot infer.
  // `domains.list` carries `consecutive_probe_failures`, which is the ONLY evidence a live
  // domain has stopped serving — nothing demotes its status (Q1).
  const domains = useQuery({ queryKey: ["domains"], queryFn: () => api.domains() });
  // U59: the usage check's subject. Not in `loading` or `failure`: until it answers the
  // check is absent, which is true — nothing has been counted — and the card still renders.
  const usage = useQuery({ queryKey: ["usage", "current"], queryFn: () => api.usage() });
  // U60: the billing check's subject. Shares `LiveStrip`'s cache entry, so no second fetch.
  const org = useQuery({
    queryKey: ["orgs", "current"],
    queryFn: () => api.currentOrg(),
  });

  // `org` is in both: the billing check has no `reads`, so without it the check ticks clear.
  const loading =
    recalls.isPending ||
    prefixes.isPending ||
    counts.isPending ||
    domains.isPending ||
    org.isPending;
  const failure =
    recalls.error ?? prefixes.error ?? counts.error ?? domains.error ?? org.error;

  const catalogue = useMemo(
    () => catalogueCounts(counts.data?.counts ?? {}),
    [counts.data],
  );

  const stack = useMemo(
    () =>
      attentionStack({
        recalls: recalls.data?.recalls ?? [],
        domains: domains.data?.domains ?? [],
        prefixes: prefixes.data?.prefixes ?? [],
        draftCount: catalogue.draft,
        usage: usage.data?.counters ?? [],
        usageCountedFrom: usage.data === undefined ? null : countedFromOf(usage.data),
        ...(org.data === undefined ? {} : { billing: org.data }),
      }),
    [recalls.data, domains.data, prefixes.data, catalogue.draft, usage.data, org.data],
  );

  const checks = applicableChecks({
    domains: domains.data?.domains.length ?? 0,
    usage: usage.data?.counters.length ?? 0,
  });

  if (loading) {
    // The skeleton keeps the strip's geometry including the dot — screens.md §2's
    // loading row. The dot is already grey in the loaded state, so there is nothing
    // here that turns a colour on.
    return (
      <Surface>
        <p className="pp-dash__quiet">Loading your catalogue&hellip;</p>
      </Surface>
    );
  }

  if (failure !== null) {
    const problem = problemOf(failure);
    return (
      <Surface tone="error" emphasis="filled">
        <div className="pp-dash__error">
          <span className="pp-dash__item-title">
            {problem === null ? "We could not reach the dashboard" : problem.title}
          </span>
          <p className="pp-dash__error-detail">
            {problem === null
              ? "Your printed codes are unaffected — the resolver serves them from its own mirror and does not depend on this screen. Reload to try again."
              : problem.detail}
          </p>
        </div>
      </Surface>
    );
  }

  // Whether the catalogue is empty **at all** — the sum of every status, not the length
  // of a page. An empty first page and an empty catalogue are different states, and only
  // one of them should show onboarding.
  const total =
    catalogue.published + catalogue.draft + catalogue.archived + catalogue.unavailable;

  return (
    <>
      <LiveStrip published={catalogue.published} />

      {total === 0 ? (
        <>
          {/* A failing domain needs no product: production hid id.finemyfriends.com behind Onboarding, 2026-09-18. */}
          {stack.length > 0 ? (
            <AttentionCardSurface stack={stack} checks={checks} />
          ) : null}
          <Onboarding />
        </>
      ) : (
        <div className="pp-dash__grid">
          <AttentionCardSurface stack={stack} checks={checks} />

          <Surface>
            <div className="pp-dash__section-title">Your catalogue</div>
            <div className="pp-dash__counts">
              <CountRow label="Published" value={catalogue.published} />
              <CountRow label="Drafts" value={catalogue.draft} />
              <CountRow label="Archived" value={catalogue.archived} />
              {/* The shopper's words, per the vocabulary rules. Never "tombstoned". */}
              <CountRow label="No longer available" value={catalogue.unavailable} />
            </div>
          </Surface>
        </div>
      )}
    </>
  );
}

function CountRow({ label, value }: { label: string; value: number }): ReactNode {
  return (
    <div className="pp-dash__count-row">
      <span className="pp-dash__count-label">{label}</span>
      <span className="pp-dash__count-value">{value}</span>
    </div>
  );
}
